import {sxPrint} from '@/utils/chakraProps';
import {isSafari} from '@/utils/detectNavigator';
import {InfoIcon} from '@chakra-ui/icons';
import {
  Box,
  HStack,
  LinkBox,
  Skeleton,
  SystemProps,
  SystemStyleObject,
  Table,
  TableCellProps,
  TableHeadProps,
  TableProps,
  TableRowProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  omitThemingProps,
} from '@chakra-ui/react';
import {
  CellContext,
  HeaderGroup,
  Row,
  RowData,
  TableOptions,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, {ReactNode} from 'react';

// Définition des parties possibles de la table qui peuvent être stylisées
type TablePart = 'table' | 'tr' | 'td' | 'th' | 'thead' | 'tbody';

export interface ResponsiveStyles {
  mobile?: Partial<Record<TablePart, SystemStyleObject>>;
  desktop?: Partial<Record<TablePart, SystemStyleObject>>;
}

export interface DataTableProps<TResourceData>
  extends TableProps, Omit<TableOptions<TResourceData>, 'getCoreRowModel'> {
  isLoading?: boolean;
  getCoreRowModel?: TableOptions<TResourceData>['getCoreRowModel'];
  footer?: React.ReactElement;
  empty?: React.ReactNode;
  responsiveStyles?: ResponsiveStyles;
  onRowClick?: (row: TResourceData) => void;
}

declare module '@tanstack/table-core' {
  interface ColumnMeta<TData extends RowData, TValue> {
    // skeleton?: (column: ColumnDef<TData, TValue>) => React.ReactElement;
    tdProps?: TableCellProps;
    // Libellé de la colonne au-dessus de chaque valeur sur téléphone, quand l'en-tête n'est pas une
    // simple chaîne (en-tête avec infobulle, par exemple).
    mobileHeading?: string;
    isNumeric?: boolean;
    getColSpan?: (context: CellContext<TData, TValue>) => number | undefined;
  }
}

const trStyles: SystemProps = {
  display: {base: 'block', lg: 'table-row'},
  borderWidth: {base: '1px', lg: 0},
  borderRadius: 'md',
  my: {base: 'md', lg: 0},
  _first: {
    mt: 0,
  },
  _last: {
    mb: 0,
  },
};

const trStylePrint: SystemProps = {
  display: 'table-row',
};

const tdStyles: (heading?: string, isNumeric?: boolean) => SystemProps = (
  heading,
  isNumeric,
) => ({
  textAlign: isNumeric ? 'right' : 'left',
  display: {base: 'flex', lg: 'table-cell'},
  flexDirection: {base: 'column', lg: 'row'},
  alignItems: {base: 'stretch', lg: 'center'},
  gap: {base: '2', lg: '0'},
  width: {base: '100%', lg: 'auto'},
  px: {base: '4', lg: 'xs'},
  py: {base: '4', lg: 'sm'},
  pl: {lg: '0'},
  borderStyle: {
    base: 'none',
    lg: 'solid',
  },
  fontSize: {
    base: 'sm',
    lg: 'xs',
  },
  position: 'relative',
  _before: {
    content: heading ? `"${heading}"` : 'none',
    display: {base: 'block', lg: 'none'},
    fontWeight: 'bold',
    color: 'secondaryGreen.500',
    width: '100%',
    textAlign: 'left',
    marginBottom: '2',
  },
  // separateur entre les lignes en mobile
  _after: {
    content: '""',
    display: {base: 'block', lg: 'none'},
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '1px',
    backgroundColor: 'gray.200',
  },
  // suppression du dernier separateur
  ':last-of-type': {
    _after: {
      display: 'none',
    },
  },
  _odd: {
    bg: {base: 'ivory.100.7', lg: 'transparent'},
  },
});

const tdStylePrint = (isNumeric?: boolean): SystemProps => ({
  display: 'table-cell',
  textAlign: isNumeric ? 'right' : 'left',
  px: 'md',
  py: 'xs',
  _before: {
    content: 'none',
  },
  _odd: {
    bg: 'transparent',
  },
});

interface DataTableHeaderProps<TResourceData> extends Omit<
  TableHeadProps,
  'children'
> {
  headerGroups: HeaderGroup<TResourceData>[];
  responsiveStyles?: ResponsiveStyles;
}

interface DataTableRowProps<TResourceData> extends Omit<
  TableRowProps,
  'children'
> {
  row: Row<TResourceData>;
  enableSelection?: boolean;
  responsiveStyles?: ResponsiveStyles;
  onClickRow?: () => void;
}

export const HeaderWithTooltip: React.FC<{
  text: string;
  tooltip: string | ReactNode;
}> = ({text, tooltip}) => (
  <Tooltip label={tooltip}>
    <HStack
      spacing={1}
      as='span'
      cursor='help'
    >
      <Text>{text}</Text>
      <InfoIcon boxSize='12px' />
    </HStack>
  </Tooltip>
);

function DataTableHeader<TResourceData>({
  headerGroups,
  responsiveStyles,
}: DataTableHeaderProps<TResourceData>) {
  // TODO: support multiple headerGroups ?
  const headerGroup = headerGroups[headerGroups.length - 1];

  return (
    <Thead
      display={{base: 'none', lg: 'table-header-group'}}
      sx={{
        ...sxPrint({display: 'table-header-group'}),
        '@media (max-width: 1024px)': {
          ...(responsiveStyles?.mobile?.thead ?? {}),
        },
        '@media (min-width: 1025px)': {
          ...(responsiveStyles?.desktop?.thead ?? {}),
        },
      }}
    >
      <Tr
        key={headerGroup.id}
        sx={{
          '@media (max-width: 1024px)': {
            ...(responsiveStyles?.mobile?.tr ?? {}),
          },
          '@media (min-width: 1025px)': {
            ...(responsiveStyles?.desktop?.tr ?? {}),
          },
        }}
      >
        {headerGroup.headers.map((header) => (
          <Th
            key={header.id}
            isNumeric={header.column.columnDef.meta?.isNumeric}
            colSpan={header.colSpan}
            p='xs'
            pl={0}
            sx={{
              width: header.column.columnDef.meta?.tdProps?.width,
              '@media (max-width: 1024px)': {
                ...(responsiveStyles?.mobile?.th ?? {}),
              },
              '@media (min-width: 1025px)': {
                ...(responsiveStyles?.desktop?.th ?? {}),
              },
            }}
          >
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </Th>
        ))}
      </Tr>
    </Thead>
  );
}

function DataTableRow<TResourceData>({
  row,
  enableSelection,
  responsiveStyles,
  onClickRow,
  ...otherProps
}: DataTableRowProps<TResourceData>) {
  let colSpanIndex: number | undefined;

  return (
    <LinkBox
      as={Tr}
      className={enableSelection ? 'selectable' : undefined}
      sx={{
        // Application d'abord des styles responsifs de base
        ...trStyles,
        ...(onClickRow ? {cursor: 'pointer'} : {}),
        // Application des surcharges responsives
        '@media (max-width: 1024px)': {
          ...(responsiveStyles?.mobile?.tr ?? {}),
        },
        '@media (min-width: 1025px)': {
          ...(responsiveStyles?.desktop?.tr ?? {}),
        },
        ...sxPrint(trStylePrint),
        // Appliqucation du sx de l'instance en dernier
        ...otherProps.sx,
      }}
      onClick={onClickRow}
    >
      {row.getVisibleCells().map((cell, index) => {
        if (undefined !== colSpanIndex && index < colSpanIndex) {
          return null;
        }

        const colSpan = cell.column.columnDef.meta?.getColSpan?.(
          cell.getContext(),
        );

        colSpanIndex = colSpan ? index + colSpan : undefined;

        // Récupérer les styles de base pour les cellules
        const baseTdStyles = tdStyles(
          'string' === typeof cell.column.columnDef.header
            ? cell.column.columnDef.header
            : cell.column.columnDef.meta?.mobileHeading,
          cell.column.columnDef.meta?.isNumeric,
        );

        const isNumeric = cell.column.columnDef.meta?.isNumeric;

        return (
          <Td
            key={cell.id}
            sx={{
              // Application d'abord des styles responsifs de base
              ...baseTdStyles,
              // Application des surcharges responsives
              '@media (max-width: 1024px)': {
                ...(responsiveStyles?.mobile?.td ?? {}),
              },
              '@media (min-width: 1025px)': {
                ...(responsiveStyles?.desktop?.td ?? {}),
              },
              ...sxPrint(tdStylePrint(isNumeric)),
              ...(cell.column.columnDef.meta?.tdProps?.sx ?? {}),
            }}
            isNumeric={cell.column.columnDef.meta?.isNumeric}
            colSpan={colSpan}
            pos={isSafari ? 'relative' : undefined}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Td>
        );
      })}
    </LinkBox>
  );
}

export default function DataTable<TResourceData>({
  columns,
  isLoading,
  footer,
  data,
  empty,
  responsiveStyles,
  onRowClick,
  ...props
}: DataTableProps<TResourceData>) {
  const options = omitThemingProps(props);

  const table = useReactTable<TResourceData>({
    columns,
    data,
    ...options,
    getCoreRowModel: options.getCoreRowModel ?? getCoreRowModel(),
  });

  // Combiner les styles par défaut avec les styles responsifs pour la table
  const tableStyles = {
    ...(responsiveStyles?.mobile?.table ?? {}),
    '@media (min-width: 1025px)': {
      ...(responsiveStyles?.desktop?.table ?? {}),
    },
  };

  if (isLoading) {
    return (
      <Table
        {...props}
        sx={tableStyles}
      >
        <DataTableHeader
          headerGroups={table.getHeaderGroups()}
          responsiveStyles={responsiveStyles}
        />
        <Tbody
          sx={{
            // Application directement du SystemStyleObject pour le tbody mobile/desktop
            '@media (max-width: 1024px)': {
              ...(responsiveStyles?.mobile?.tbody ?? {}),
            },
            '@media (min-width: 1025px)': {
              ...(responsiveStyles?.desktop?.tbody ?? {}),
            },
          }}
        >
          {Array(5)
            .fill(null)
            .map((_, rowIndex) => (
              <Tr
                key={`skeleton_row_${rowIndex}`}
                sx={{
                  ...trStyles,
                  '@media (max-width: 1024px)': {
                    ...(responsiveStyles?.mobile?.tr ?? {}),
                  },
                  '@media (min-width: 1025px)': {
                    ...(responsiveStyles?.desktop?.tr ?? {}),
                  },
                }}
              >
                {columns.map((column, colIndex) => (
                  <Td
                    key={`skeleton_cell_${rowIndex}_${colIndex}`}
                    sx={{
                      ...tdStyles(),
                      '@media (max-width: 1024px)': {
                        ...(responsiveStyles?.mobile?.td ?? {}),
                      },
                      '@media (min-width: 1025px)': {
                        ...(responsiveStyles?.desktop?.td ?? {}),
                      },
                    }}
                    {...column.meta?.tdProps}
                  >
                    <Box p='10px'>
                      <Skeleton
                        height='20px'
                        width='100%'
                      />
                    </Box>
                  </Td>
                ))}
              </Tr>
            ))}
        </Tbody>
        {footer}
      </Table>
    );
  }

  if (data.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <Table
      {...props}
      sx={tableStyles}
    >
      <DataTableHeader
        headerGroups={table.getHeaderGroups()}
        responsiveStyles={responsiveStyles}
      />

      <Tbody
        sx={{
          // Application directement du SystemStyleObject pour le tbody mobile/desktop
          '@media (max-width: 1024px)': {
            ...(responsiveStyles?.mobile?.tbody ?? {}),
          },
          '@media (min-width: 1025px)': {
            ...(responsiveStyles?.desktop?.tbody ?? {}),
          },
        }}
      >
        {table.getRowModel().rows.map((row) => (
          <DataTableRow
            key={row.id}
            row={row}
            enableSelection={!!options.enableRowSelection}
            responsiveStyles={responsiveStyles}
            onClickRow={
              onRowClick
                ? () => {
                    onRowClick(row.original);
                  }
                : undefined
            }
          />
        ))}
      </Tbody>

      {footer}
    </Table>
  );
}
